import React, { useEffect, useState } from "react";
import { EventBus } from "../../engine/EventBus.js";
import {
  AUTOSAVE_SLOT_ID,
  load as loadSlot,
} from "../../engine/persistence/PersistenceManager.js";
import type { LoadSaveFileResult } from "../../engine/persistence/SaveFile.js";

/**
 * Party-wipe modal (P09).
 *
 * Subscribes to `world:party_wiped` and overlays a blocking screen that
 * offers "Load last autosave" or "Return to main menu". The modal stays
 * mounted until the host dismisses it (typically after a successful load
 * clears the autosave slot).
 */

interface Props {
  readonly onReturnToMenu: () => void;
  readonly onLoadComplete?: (result: LoadSaveFileResult) => void;
}

export function PartyWipeModal({ onReturnToMenu, onLoadComplete }: Props) {
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    return EventBus.on("world:party_wiped", () => {
      setVisible(true);
      setStatus(null);
    });
  }, []);

  if (!visible) return null;

  const handleLoad = async () => {
    setBusy(true);
    try {
      const result = await loadSlot(AUTOSAVE_SLOT_ID);
      setStatus("Autosave restored.");
      onLoadComplete?.(result);
      setVisible(false);
    } catch (err) {
      setStatus(`Load failed: ${describeError(err)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="party-wipe-modal"
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(0,0,0,0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 300,
        pointerEvents: "auto",
      }}
    >
      <div
        style={{
          background: "#1a1a1a",
          border: "1px solid rgba(255,80,80,0.4)",
          borderRadius: 8,
          padding: 32,
          color: "white",
          fontFamily: "monospace",
          display: "flex",
          flexDirection: "column",
          gap: 14,
          minWidth: 320,
          textAlign: "center",
        }}
      >
        <h2 style={{ margin: 0, fontSize: 22, color: "#f66", letterSpacing: 2 }}>
          THE PARTY HAS FALLEN
        </h2>
        <p style={{ margin: 0, color: "#aaa", fontSize: 12 }}>
          The dungeon swallowed every breath.
        </p>
        <button type="button" onClick={handleLoad} disabled={busy} style={btn(busy)}>
          Load last autosave
        </button>
        <button type="button" onClick={onReturnToMenu} disabled={busy} style={btn(busy)}>
          Return to main menu
        </button>
        {status && <div style={{ fontSize: 11, color: "#9f9" }}>{status}</div>}
      </div>
    </div>
  );
}

function btn(disabled: boolean): React.CSSProperties {
  return {
    background: disabled ? "#222" : "#333",
    color: disabled ? "#555" : "white",
    border: "1px solid",
    borderColor: disabled ? "#333" : "rgba(255,255,255,0.3)",
    borderRadius: 4,
    padding: "8px 16px",
    fontFamily: "monospace",
    fontSize: 13,
    cursor: disabled ? "default" : "pointer",
  };
}

function describeError(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}
